import { useCallback, useContext } from "react";
import { defineMessages } from "react-intl";
import { ApolloError } from "@apollo/client";
import { useQuery } from "@app/utils";
import { UppAuthContext } from "@app/provider";
import { ErrorBody } from "@app/provider/user";
import { ResultCodes } from "@app/service/resultCodes";

/**
 * UPP-5864/5865: the client seam for the DOB-proof (SignupIntent) handle used by
 * the tokenized sign-up gates. It owns both halves of one invariant:
 *
 *  - READ (`intentToken`): the effective handle, from either the in-app DOB
 *    verify (context `intentToken`, UPP-5864) or a guest flow that verified DOB
 *    server-side and carries the opaque handle in the URL as `si` (UPP-5865).
 *  - RECOVER (`handleReverificationRequired`): on a server proof rejection,
 *    records the rejected handle (`rejectedIntentHandle`) and clears the in-app
 *    handle, sending the user back to the DOB screen instead of degrading into
 *    the OTP/activation flow.
 *
 * `rejectedIntentHandle` is the coupling between the two halves: RECOVER writes
 * it, READ honors it (a handle already rejected this session won't re-arm the
 * DOB skip), and the DOB screen raises the re-verify banner off it. Scoping to
 * the handle (not a sticky bool) lets a fresh, different handle re-arm cleanly.
 */

// Owned here (the recovery seam) but rendered by the DOB screen on mount.
export const identityReverificationMessages = defineMessages({
  identityReverificationRequired: {
    id: "identityReverificationRequired",
    defaultMessage:
      "Your verification is no longer valid. Please re-enter your date of birth to continue.",
  },
});

const getErrorCode = (err: unknown): string | undefined =>
  (
    (err as ApolloError)?.graphQLErrors?.[0]?.extensions?.exception as
      | ErrorBody
      | undefined
  )?.code;

// Stable notification id so repeated proof failures de-dup (the provider skips
// adding a notification whose id already exists) and so the DOB screen can clear
// it on resubmit.
export const IDENTITY_REVERIFICATION_NOTIFICATION_ID =
  "IDENTITY_REVERIFICATION_REQUIRED";

export const useSignupIntent = (): {
  intentToken: string | undefined;
  handleReverificationRequired: (err: unknown) => boolean;
} => {
  const query = useQuery();
  const {
    intentToken: contextIntentToken,
    rejectedIntentHandle,
    setIntentToken,
    setRejectedIntentHandle,
    setVerifiedProfileByToken,
  } = useContext(UppAuthContext);

  const si = query.get("si") ?? undefined;

  // Context (in-app verify) wins; otherwise the URL handle, unless it's the one
  // the server already rejected this session (so we don't re-arm a known-bad
  // handle). A fresh, different `si` on a later navigation is unaffected.
  const intentToken =
    contextIntentToken ?? (si && si !== rejectedIntentHandle ? si : undefined);

  const handleReverificationRequired = useCallback(
    (err: unknown): boolean => {
      if (getErrorCode(err) !== ResultCodes.IDENTITY_REVERIFICATION_REQUIRED) {
        return false;
      }

      // Record the rejected handle (guest `si` or in-app intentToken). This both
      // drops the DOB skip for it and, on the DOB screen, drives the re-verify
      // banner. No handle means a bare first-load with no proof — nothing to nag.
      const rejectedHandle = si ?? contextIntentToken;
      if (rejectedHandle) {
        setRejectedIntentHandle(rejectedHandle);
      }

      setIntentToken(undefined);
      setVerifiedProfileByToken(false);
      return true;
    },
    [
      contextIntentToken,
      si,
      setIntentToken,
      setRejectedIntentHandle,
      setVerifiedProfileByToken,
    ]
  );

  return { intentToken, handleReverificationRequired };
};

export default useSignupIntent;
